import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../../components/common/Button';
import { LookupFieldSelect } from '../../components/formFields/LookupFieldSelect';
import WizardMultiSelectChips from '../../components/ui/WizardMultiSelectChips';
import { Modal } from '../../components/common/Modal';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import WizardSelect from '../../components/ui/WizardSelect';
import { FieldError, inpClass } from '../task-case/criminalFieldUi';
import { listTimeUnits, type TimeUnitRow } from '../../services/caseRequestSubsidiaryTasks';
import { listActiveRoles, type ActiveRoleRow } from '../../services/roleAdministration';
import {
  buildRevertStageOptions,
  getEscalationEligibleUsers,
  mapRevertKeysToStageIds,
  resolveDefaultNextStageLabel,
  getWorkflowTemplateStageRevertOptions,
  resolveWorkflowTemplateStageId,
} from '../../services/workflowTemplateStageConfig';
import { fileCategoryExemptFromSubFileCategory, subFileCategoryRequiredForCategory } from '../../utils/subFileCategoryRules';
import {
  createDefaultDesignerStages,
  displayNextVersionNo,
  findActiveWorkflowTemplateInDetails,
  getAllocationOfficers,
  resolveGeneratedWorkflowCode,
  shouldRetryWorkflowCodeValidation,
  collectExistingWorkflowCodes,
  countWorkflowTemplatesForCodeGeneration,
  getFileCategories,
  getLatestWorkflowVersion,
  getLetterTemplates,
  getSubFileCategories,
  getWorkflowTemplateById,
  getWorkflowTemplateDetails,
  saveActiveTemplateAllocationOfficer,
  saveWorkflowTemplate,
  syncDesignerStagesFromSavedStages,
  validateWorkflowTemplate,
  type WorkflowValidationCheck,
  type AllocationOfficerOption,
  type DesignerCanvasStage,
  type LetterTemplateOption,
  type WorkflowEscalationConfig,
  type WorkflowKpiConfig,
  type WorkflowRoutingConfig,
  type WorkflowStageConfigState,
  type WorkflowTemplateDesignerLoadResult,
  type WorkflowTemplateDetailItem,
} from '../../services/workflow';
import type { LookupItem } from '../../mocks/criminalMasterData';
import { alSelClass } from '../../utils/alSelClass';
import { formatDocumentTableDateTime } from '../../utils/dateFormat';
import { setFlashToast } from '../../utils/flashToast';

/** Master-data / API domain strings — shown in English regardless of UI language. */
const ASSIGNED_USER_RECIPIENT = 'Assigned User';
const WORKFLOW_ADD_NEXT_STAGE = '+ Add Next Stage';
const WORKFLOW_REMOVE_STAGE = 'Remove Stage';
const ACTIVE_TEMPLATE_CONFLICT_WARNING =
  'A workflow template already exists for this File Category and Sub File Category combination. Please make sure that you are not attempting to replace an existing active workflow template.';

function formatStageBadge(num: number): string {
  return `Stage ${num}`;
}

function formatStageOption(num: number, label: string): string {
  return `Stage ${num} - ${label.trim()}`;
}

// ─── Types ────────────────────────────────────────────────────────────────────

type DesignerMode = 'create' | 'edit' | 'view';
type LoadState = 'idle' | 'loading' | 'error';

type TemplateForm = {
  workflowTemplateName: string;
  fileCategoryId: string;
  subFileCategoryId: string;
  allocationOfficerId: string;
  letterTemplateIds: string[];
};

type TemplateFormErrors = Partial<Record<keyof TemplateForm, string>>;

type StageConfig = WorkflowStageConfigState;

function enrichStageConfigsKpiUnitIds(
  configs: Record<string, StageConfig>,
  timeUnits: TimeUnitRow[],
): Record<string, StageConfig> {
  const daysUnit =
    timeUnits.find((x) => (x.code ?? '').toUpperCase() === 'DAYS') ??
    timeUnits.find((x) => /day/i.test(x.name ?? ''));
  const daysId = daysUnit?.id?.trim() ?? '';
  if (!daysId) return configs;

  let changed = false;
  const next: Record<string, StageConfig> = {};

  for (const [stageId, cfg] of Object.entries(configs)) {
    const kpis = cfg.kpis;
    if (!cfg.kpiConfigured || !kpis) {
      next[stageId] = cfg;
      continue;
    }

    const patchUnit = (value: number | null | undefined, unitId: string | null | undefined): string | null => {
      if (value == null || value <= 0) return unitId ?? null;
      return unitId?.trim() || daysId;
    };

    const enriched: WorkflowKpiConfig = {
      ...kpis,
      ttcTimeUnitId: patchUnit(kpis.ttc, kpis.ttcTimeUnitId),
      ttrTimeUnitId: patchUnit(kpis.ttr, kpis.ttrTimeUnitId),
      tteTimeUnitId: patchUnit(kpis.tte, kpis.tteTimeUnitId),
    };

    if (
      enriched.ttcTimeUnitId !== kpis.ttcTimeUnitId ||
      enriched.ttrTimeUnitId !== kpis.ttrTimeUnitId ||
      enriched.tteTimeUnitId !== kpis.tteTimeUnitId
    ) {
      changed = true;
      next[stageId] = { ...cfg, kpis: enriched };
    } else {
      next[stageId] = cfg;
    }
  }

  return changed ? next : configs;
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function WorkflowTemplateDesignerPage() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const [sp, setSearchParams] = useSearchParams();

  const templateIdFromUrl = sp.get('templateId') ?? '';
  const mode = (sp.get('mode') as DesignerMode | null) ?? 'create';
  const isCreate = mode === 'create';
  const isReadOnly = mode === 'view';

  const [form, setForm] = useState<TemplateForm>({
    workflowTemplateName: '',
    fileCategoryId: '',
    subFileCategoryId: '',
    allocationOfficerId: '',
    letterTemplateIds: [],
  });
  const [formErrors, setFormErrors] = useState<TemplateFormErrors>({});
  const [workflowCode, setWorkflowCode] = useState('');
  const [apiWorkflowCode, setApiWorkflowCode] = useState('');
  const [versionNo, setVersionNo] = useState(isCreate ? '1' : '');
  const [workflowTemplateId, setWorkflowTemplateId] = useState(templateIdFromUrl || '');
  const [status, setStatus] = useState<'Draft' | 'Active' | 'Inactive'>('Draft');
  const [saving, setSaving] = useState(false);
  const [validating, setValidating] = useState(false);
  const [replaceActiveOpen, setReplaceActiveOpen] = useState(false);
  const [activeConflictSubCategoryId, setActiveConflictSubCategoryId] = useState<string | null>(null);

  const [fileCategories, setFileCategories] = useState<LookupItem[]>([]);
  const [subFileCategories, setSubFileCategories] = useState<LookupItem[]>([]);
  const [allocationOfficers, setAllocationOfficers] = useState<AllocationOfficerOption[]>([]);
  const [letterTemplates, setLetterTemplates] = useState<LetterTemplateOption[]>([]);
  const [timeUnits, setTimeUnits] = useState<TimeUnitRow[]>([]);
  const [closureRoles, setClosureRoles] = useState<ActiveRoleRow[]>([]);
  const [fileCategoriesState, setFileCategoriesState] = useState<LoadState>('loading');
  const [subFileCategoriesState, setSubFileCategoriesState] = useState<LoadState>('idle');
  const [allocationOfficersState, setAllocationOfficersState] = useState<LoadState>('loading');
  const [letterTemplatesState, setLetterTemplatesState] = useState<LoadState>('idle');
  const [timeUnitsState, setTimeUnitsState] = useState<LoadState>('loading');
  const [templateLoadState, setTemplateLoadState] = useState<LoadState>(isCreate ? 'idle' : 'loading');
  const [templateUpdatedAt, setTemplateUpdatedAt] = useState('');
  const [templateUpdatedBy, setTemplateUpdatedBy] = useState('');
  const [originalAllocationOfficerId, setOriginalAllocationOfficerId] = useState('');
  const [activeOfficerSaveError, setActiveOfficerSaveError] = useState('');

  const templateId = templateIdFromUrl;
  const isActiveOfficerEditMode = status === 'Active' && mode === 'edit';
  const allocationOfficerChanged =
    isActiveOfficerEditMode &&
    form.allocationOfficerId.trim() !== originalAllocationOfficerId.trim();

  const [canvasStages, setCanvasStages] = useState<DesignerCanvasStage[]>(() =>
    isCreate ? createDefaultDesignerStages() : [],
  );
  const [selectedStageId, setSelectedStageId] = useState('');
  const [stageConfigs, setStageConfigs] = useState<Record<string, StageConfig>>({});

  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [routingOpen, setRoutingOpen] = useState(false);
  const [kpiOpen, setKpiOpen] = useState(false);
  const [escalationOpen, setEscalationOpen] = useState(false);
  const [assignmentOpen, setAssignmentOpen] = useState(false);
  const [validationOpen, setValidationOpen] = useState(false);
  const [validationChecks, setValidationChecks] = useState<WorkflowValidationCheck[]>([]);
  const [validated, setValidated] = useState(false);

  const selectedStage = canvasStages.find((s) => s.id === selectedStageId) ?? null;
  const nextCustomStageIdRef = useRef(1);
  const workflowDetailsCountRef = useRef(0);
  const workflowTemplateDetailsRef = useRef<WorkflowTemplateDetailItem[]>([]);
  const latestGeneratedWorkflowCodeRef = useRef('');
  const workflowMetadataGenerationRef = useRef(0);
  const workflowMetadataRefreshRef = useRef<Promise<string> | null>(null);
  const skipTemplateLoadRef = useRef(false);
  const showActiveTemplateConflict = Boolean(
    form.subFileCategoryId && activeConflictSubCategoryId === form.subFileCategoryId,
  );
  const usesApiWorkflowCode = !isCreate && status === 'Active';
  const displayedWorkflowCode = usesApiWorkflowCode ? apiWorkflowCode : workflowCode;
  const showDraftVersionCodeNote = Boolean(
    status === 'Draft' && versionNo.trim() && displayedWorkflowCode.trim(),
  );

  const selectedFileCategory = useMemo(
    () => fileCategories.find((c) => c.id === form.fileCategoryId),
    [fileCategories, form.fileCategoryId],
  );

  const isExemptCategory = Boolean(
    selectedFileCategory && fileCategoryExemptFromSubFileCategory(selectedFileCategory),
  );

  const subFileCategoryRequired = useMemo(
    () =>
      subFileCategoryRequiredForCategory(
        selectedFileCategory ?? { id: form.fileCategoryId },
        subFileCategories,
        subFileCategoriesState === 'loading',
      ),
    [selectedFileCategory, form.fileCategoryId, subFileCategories, subFileCategoriesState],
  );

  const subFileCategoryHasNoOptions =
    Boolean(form.fileCategoryId) &&
    subFileCategoriesState === 'idle' &&
    subFileCategories.length === 0;

  function setField<K extends keyof TemplateForm>(key: K, value: string) {
    if (key === 'fileCategoryId') {
      setSubFileCategories([]);
      setSubFileCategoriesState('idle');
      setLetterTemplates([]);
      setLetterTemplatesState('idle');
      setActiveConflictSubCategoryId(null);
      if (isCreate) {
        clearGeneratedWorkflowCode();
        setVersionNo('1');
      }
    }
    if (key === 'subFileCategoryId') {
      setLetterTemplates([]);
      setLetterTemplatesState('idle');
      setActiveConflictSubCategoryId(null);
      if (isCreate) {
        clearGeneratedWorkflowCode();
        setVersionNo('1');
      }
    }
    setForm((prev) => {
      const next = { ...prev, [key]: value };
      if (key === 'fileCategoryId') {
        next.subFileCategoryId = '';
        next.letterTemplateIds = [];
      }
      if (key === 'subFileCategoryId') {
        next.letterTemplateIds = [];
      }
      return next;
    });
    if (value.trim()) {
      setFormErrors((e) => {
        const n = { ...e };
        delete n[key];
        return n;
      });
    }
    if (key === 'allocationOfficerId') {
      setActiveOfficerSaveError('');
    }
    setValidated(false);
  }

  function setLetterTemplateIds(ids: string[]) {
    setForm((prev) => ({ ...prev, letterTemplateIds: ids }));
    if (ids.length) {
      setFormErrors((e) => {
        const n = { ...e };
        delete n.letterTemplateIds;
        return n;
      });
    }
    setValidated(false);
  }

  const letterTemplatesCanLoad = Boolean(form.subFileCategoryId.trim());

  const letterTemplateDisabled =
    isReadOnly ||
    status === 'Active' ||
    !letterTemplatesCanLoad ||
    letterTemplatesState === 'loading';

  useEffect(() => {
    if (!letterTemplatesCanLoad) {
      queueMicrotask(() => {
        setLetterTemplates([]);
        setLetterTemplatesState('idle');
      });
      return;
    }

    const subCategoryId = form.subFileCategoryId.trim();
    let cancelled = false;

    (async () => {
      await Promise.resolve();
      if (cancelled) return;
      setLetterTemplatesState('loading');
      const res = await getLetterTemplates(subCategoryId);
      if (cancelled) return;
      if (res.success) {
        setLetterTemplates(res.data);
        setLetterTemplatesState('idle');
      } else {
        setLetterTemplates([]);
        setLetterTemplatesState('error');
        setFlashToast({ message: res.message, variant: 'error' });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [form.subFileCategoryId, letterTemplatesCanLoad]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await Promise.resolve();
      if (cancelled) return;
      setFileCategoriesState('loading');
      const [catsRes, officersRes, unitsRes, rolesRes] = await Promise.all([
        getFileCategories(),
        getAllocationOfficers(),
        listTimeUnits(),
        listActiveRoles(),
      ]);
      if (cancelled) return;
      if (catsRes.success) {
        setFileCategories(catsRes.data);
        setFileCategoriesState('idle');
      } else {
        setFileCategoriesState('error');
        setFlashToast({ message: catsRes.message ?? 'Request failed.', variant: 'error' });
      }
      if (officersRes.success) {
        setAllocationOfficers(officersRes.data);
        setAllocationOfficersState('idle');
      } else {
        setAllocationOfficersState('error');
        setFlashToast({ message: officersRes.message, variant: 'error' });
      }
      if (unitsRes.success) {
        setTimeUnits(unitsRes.data);
        setTimeUnitsState('idle');
      } else {
        setTimeUnits([]);
        setTimeUnitsState('error');
      }
      if (rolesRes.success) setClosureRoles(rolesRes.data);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!form.fileCategoryId) return;

    let cancelled = false;
    (async () => {
      await Promise.resolve();
      if (cancelled) return;
      setSubFileCategoriesState('loading');
      const res = await getSubFileCategories(form.fileCategoryId);
      if (cancelled) return;
      if (res.success) {
        setSubFileCategories(res.data);
        setSubFileCategoriesState('idle');
      } else {
        setSubFileCategories([]);
        setSubFileCategoriesState('error');
        setFlashToast({ message: res.message ?? 'Request failed.', variant: 'error' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [form.fileCategoryId]);

  function generatesWorkflowCodeLocally(): boolean {
    return isCreate || status !== 'Active';
  }

  function clearGeneratedWorkflowCode() {
    latestGeneratedWorkflowCodeRef.current = '';
    setWorkflowCode('');
  }

  function commitGeneratedWorkflowCode(code: string, nextVersionNo?: string) {
    latestGeneratedWorkflowCodeRef.current = code;
    setWorkflowCode(code);
    if (nextVersionNo != null && nextVersionNo !== versionNo) {
      setVersionNo(nextVersionNo);
    }
  }

  function storeWorkflowTemplateDetails(items: WorkflowTemplateDetailItem[]) {
    workflowTemplateDetailsRef.current = items;
    workflowDetailsCountRef.current = countWorkflowTemplatesForCodeGeneration(items);
  }

  function existingWorkflowCodesForGeneration(): string[] {
    return collectExistingWorkflowCodes(
      workflowTemplateDetailsRef.current,
      workflowTemplateId || templateIdFromUrl || undefined,
    );
  }

  function computeLocalWorkflowCode(
    subFileCategoryId: string,
    version: string,
    templateCount = workflowDetailsCountRef.current,
    subCategoryCode?: string,
  ): string {
    const subCode =
      subCategoryCode ??
      subFileCategories.find((x) => x.id === subFileCategoryId)?.code ??
      '';
    return resolveGeneratedWorkflowCode({
      subFileCategoryCode: subCode,
      fileCategoryCode: selectedFileCategory?.code,
      isExemptCategory,
      templateCount,
      isNewTemplate: isCreate,
      versionNo: version,
      existingWorkflowCodes: existingWorkflowCodesForGeneration(),
    });
  }

  async function resolveSubFileCategoryCode(subFileCategoryId: string): Promise<string> {
    const cached = subFileCategories.find((x) => x.id === subFileCategoryId)?.code?.trim() ?? '';
    if (cached || !form.fileCategoryId.trim()) return cached;

    const subRes = await getSubFileCategories(form.fileCategoryId);
    if (!subRes.success) return cached;
    return subRes.data.find((x) => x.id === subFileCategoryId)?.code?.trim() ?? '';
  }

  async function refreshWorkflowCodeMetadata(options?: {
    subFileCategoryId?: string;
    workflowTemplateName?: string;
    templateCount?: number;
  }): Promise<string> {
    if (!generatesWorkflowCodeLocally()) {
      return displayedWorkflowCode.trim();
    }

    const subFileCategoryId = (options?.subFileCategoryId ?? form.subFileCategoryId).trim();
    const workflowTemplateName = options?.workflowTemplateName ?? form.workflowTemplateName;

    if (!subFileCategoryId && !isExemptCategory) return '';

    let templateCount = options?.templateCount ?? workflowDetailsCountRef.current;
    if (options?.templateCount == null && subFileCategoryId) {
      const detailsRes = await getWorkflowTemplateDetails(subFileCategoryId);
      if (detailsRes.success) {
        storeWorkflowTemplateDetails(detailsRes.data);
        templateCount = countWorkflowTemplatesForCodeGeneration(detailsRes.data);
      }
    }

    let version = versionNo.trim() || '1';
    if (subFileCategoryId && workflowTemplateName.trim()) {
      const versionRes = await getLatestWorkflowVersion(subFileCategoryId, workflowTemplateName);
      if (!versionRes.success) {
        setFlashToast({ message: versionRes.message, variant: 'error' });
      } else {
        version = displayNextVersionNo(versionRes.data.versionNo);
      }
    } else if (!versionNo.trim()) {
      version = '1';
    }

    const subCategoryCode = subFileCategoryId
      ? await resolveSubFileCategoryCode(subFileCategoryId)
      : '';
    const code = computeLocalWorkflowCode(subFileCategoryId, version, templateCount, subCategoryCode);
    commitGeneratedWorkflowCode(code, version);
    return code;
  }

  function resolveWorkflowCodeForSave(): string {
    if (!generatesWorkflowCodeLocally()) {
      return displayedWorkflowCode.trim();
    }

    const fromRef = latestGeneratedWorkflowCodeRef.current.trim();
    if (fromRef) return fromRef;

    const fromState = workflowCode.trim();
    if (fromState) return fromState;

    if (!form.subFileCategoryId && !isExemptCategory) return '';
    return computeLocalWorkflowCode(form.subFileCategoryId, versionNo.trim() || '1');
  }

  function updateGeneratedWorkflowCode(subFileCategoryId: string, version: string) {
    if (!generatesWorkflowCodeLocally() || (!subFileCategoryId && !isExemptCategory)) return;
    commitGeneratedWorkflowCode(computeLocalWorkflowCode(subFileCategoryId, version));
  }

  async function refreshVersionNo(subFileCategoryId: string, workflowTemplateName: string) {
    await refreshWorkflowCodeMetadata({ subFileCategoryId, workflowTemplateName });
  }

  useEffect(() => {
    const generation = ++workflowMetadataGenerationRef.current;
    let cancelled = false;

    const runRefresh = async () => {
      await Promise.resolve();
      if (cancelled || generation !== workflowMetadataGenerationRef.current) return;

      // Exempt categories (RTI, Public Petition) have no sub-categories.
      if (!form.subFileCategoryId && isExemptCategory && form.fileCategoryId) {
        if (generatesWorkflowCodeLocally() && isCreate) {
          await refreshWorkflowCodeMetadata({
            subFileCategoryId: '',
            workflowTemplateName: form.workflowTemplateName,
          });
        }
        return;
      }

      if (!form.subFileCategoryId) return;

      const subFileCategoryId = form.subFileCategoryId;
      const currentTemplateId = workflowTemplateId || templateIdFromUrl || undefined;

      const detailsRes = await getWorkflowTemplateDetails(subFileCategoryId);
      if (cancelled || generation !== workflowMetadataGenerationRef.current) return;

      if (detailsRes.success) {
        storeWorkflowTemplateDetails(detailsRes.data);
        const activeTemplate = findActiveWorkflowTemplateInDetails(detailsRes.data, currentTemplateId);
        setActiveConflictSubCategoryId(activeTemplate ? subFileCategoryId : null);

        if (generatesWorkflowCodeLocally() && isCreate) {
          await refreshWorkflowCodeMetadata({
            subFileCategoryId,
            workflowTemplateName: form.workflowTemplateName,
            templateCount: countWorkflowTemplatesForCodeGeneration(detailsRes.data),
          });
        }
      } else {
        setActiveConflictSubCategoryId(null);
        if (isCreate) {
          clearGeneratedWorkflowCode();
          setFlashToast({ message: detailsRes.message, variant: 'error' });
        }
      }
    };

    workflowMetadataRefreshRef.current = runRefresh().then(() => latestGeneratedWorkflowCodeRef.current);

    return () => {
      cancelled = true;
    };
  }, [
    form.subFileCategoryId,
    form.fileCategoryId,
    form.workflowTemplateName,
    isCreate,
    isExemptCategory,
    status,
    subFileCategories,
    workflowTemplateId,
    templateIdFromUrl,
  ]);

  function handleWorkflowTemplateNameBlur() {
    if (isReadOnly || status === 'Active') return;
    void refreshVersionNo(form.subFileCategoryId, form.workflowTemplateName);
  }

  function ensureStageConfig(stageId: string): StageConfig {
    return (
      stageConfigs[stageId] ?? {
        routingConfigured: false,
        kpiConfigured: false,
        escalationConfigured: false,
      }
    );
  }

  function updateStageConfig(stageId: string, patch: Partial<StageConfig>) {
    setStageConfigs((prev) => ({
      ...prev,
      [stageId]: { ...ensureStageConfig(stageId), ...patch },
    }));
  }

  async function applyDesignerTemplateFromApi(data: WorkflowTemplateDesignerLoadResult) {
    setForm({
      workflowTemplateName: data.workflowTemplateName,
      fileCategoryId: data.fileCategoryId,
      subFileCategoryId: data.subFileCategoryId,
      allocationOfficerId: data.allocationOfficerId,
      letterTemplateIds: data.letterTemplateIds,
    });
    if (data.status === 'Active') {
      setApiWorkflowCode(data.workflowCode);
    } else {
      setApiWorkflowCode('');
      if (data.workflowCode.trim()) {
        commitGeneratedWorkflowCode(data.workflowCode.trim());
        if (data.subFileCategoryId) {
          const detailsRes = await getWorkflowTemplateDetails(data.subFileCategoryId);
          if (detailsRes.success) storeWorkflowTemplateDetails(detailsRes.data);
        }
      } else if (data.subFileCategoryId) {
        const [detailsRes, subCatsRes] = await Promise.all([
          getWorkflowTemplateDetails(data.subFileCategoryId),
          data.fileCategoryId ? getSubFileCategories(data.fileCategoryId) : Promise.resolve(null),
        ]);
        if (detailsRes.success) {
          storeWorkflowTemplateDetails(detailsRes.data);
          const sub =
            subCatsRes && subCatsRes.success
              ? subCatsRes.data.find((x) => x.id === data.subFileCategoryId)
              : undefined;
          const code = resolveGeneratedWorkflowCode({
            subFileCategoryCode: sub?.code ?? '',
            templateCount: countWorkflowTemplatesForCodeGeneration(detailsRes.data),
            isNewTemplate: false,
            versionNo: data.versionNo,
            existingWorkflowCodes: collectExistingWorkflowCodes(
              detailsRes.data,
              data.workflowTemplateId,
            ),
          });
          commitGeneratedWorkflowCode(code);
        }
      }
    }
    setVersionNo(data.versionNo);
    setWorkflowTemplateId(data.workflowTemplateId);
    setStatus(data.status);
    setOriginalAllocationOfficerId(data.allocationOfficerId);
    setCanvasStages(data.canvasStages);
    setStageConfigs(data.stageConfigs);
    setSelectedStageId(data.canvasStages[0]?.id ?? '');

    const customCount = data.canvasStages.filter((s) => s.kind === 'custom').length;
    nextCustomStageIdRef.current = customCount + 1;

    const latest = data.updateHistory[data.updateHistory.length - 1];
    if (latest) {
      setTemplateUpdatedAt(formatDocumentTableDateTime(latest.timestamp) || latest.timestamp);
      setTemplateUpdatedBy(latest.performedByName || latest.performedBy);
    } else {
      setTemplateUpdatedAt('');
      setTemplateUpdatedBy('');
    }
  }

  async function refreshWorkflowTemplateFromApi(templateId: string): Promise<boolean> {
    const res = await getWorkflowTemplateById(templateId);
    if (!res.success) {
      setFlashToast({ message: res.message, variant: 'error' });
      return false;
    }

    await applyDesignerTemplateFromApi(res.data);
    setValidated(false);
    return true;
  }

  useEffect(() => {
    if (isCreate || !templateId) return;

    let cancelled = false;
    (async () => {
      await Promise.resolve();
      if (cancelled) return;

      if (skipTemplateLoadRef.current) {
        skipTemplateLoadRef.current = false;
        setTemplateLoadState('idle');
        return;
      }

      setTemplateLoadState('loading');
      const res = await getWorkflowTemplateById(templateId);
      if (cancelled) return;

      if (!res.success) {
        setTemplateLoadState('error');
        setFlashToast({ message: res.message, variant: 'error' });
        return;
      }

      await applyDesignerTemplateFromApi(res.data);
      if (cancelled) return;

      setTemplateLoadState('idle');
    })();

    return () => {
      cancelled = true;
    };
  }, [isCreate, templateId]);

  function getStageNumber(stageId: string): number {
    return canvasStages.findIndex((s) => s.id === stageId) + 1;
  }

  function getNextStageLabel(stageId: string): string {
    const idx = canvasStages.findIndex((s) => s.id === stageId);
    const next = canvasStages[idx + 1];
    if (!next) return 'End';
    return formatStageOption(getStageNumber(next.id), next.label);
  }

  function getPreviousStageOptions(stageId: string): { id: string; label: string }[] {
    const idx = canvasStages.findIndex((s) => s.id === stageId);
    return canvasStages.slice(0, idx).map((s) => ({
      id: s.id,
      label: formatStageOption(getStageNumber(s.id), s.label),
    }));
  }

  function addStage(afterId: string) {
    const closureIdx = canvasStages.findIndex((s) => s.kind === 'closure');
    if (closureIdx < 0) return;

    const afterIdx = canvasStages.findIndex((s) => s.id === afterId);
    const insertAt = afterIdx >= 0 ? afterIdx + 1 : closureIdx;

    const id = `st-${nextCustomStageIdRef.current++}`;
    const nextStage: DesignerCanvasStage = {
      id,
      label: 'New Stage',
      kind: 'custom',
    };

    setCanvasStages((prev) => {
      const next = [...prev];
      next.splice(insertAt, 0, nextStage);
      return next;
    });
    setSelectedStageId(id);
    setValidated(false);
  }

  function removeStage(stageId: string) {
    const stage = canvasStages.find((s) => s.id === stageId);
    if (!stage || stage.kind !== 'custom') return;

    const nextStages = canvasStages.filter((s) => s.id !== stageId);
    setCanvasStages(nextStages);
    setStageConfigs((prev) => {
      const next: Record<string, StageConfig> = {};
      for (const [id, cfg] of Object.entries(prev)) {
        if (id === stageId) continue;
        if (!cfg.routing?.allowedRevertStageKeys?.includes(stageId)) {
          next[id] = cfg;
          continue;
        }
        next[id] = {
          ...cfg,
          routing: {
            ...cfg.routing,
            allowedRevertStageKeys: cfg.routing.allowedRevertStageKeys.filter((k) => k !== stageId),
          },
        };
      }
      return next;
    });
    if (selectedStageId === stageId) {
      setSelectedStageId(nextStages[0]?.id ?? '');
    }
    setValidated(false);
  }

  function renameCustomStage(stageId: string, label: string) {
    const cleaned = label.replace(/[^a-zA-Z0-9 ]/g, '').slice(0, 255);
    setCanvasStages((prev) =>
      prev.map((s) => (s.id === stageId && s.kind === 'custom' ? { ...s, label: cleaned } : s)),
    );
  }

  function validateForm(): TemplateFormErrors {
    const errors: TemplateFormErrors = {};
    if (!form.workflowTemplateName.trim()) errors.workflowTemplateName = t('workflow.designer.errors.templateNameRequired');
    if (!form.fileCategoryId) errors.fileCategoryId = t('workflow.designer.errors.fileCategoryRequired');
    if (subFileCategoryRequired && !form.subFileCategoryId) {
      errors.subFileCategoryId = t('workflow.designer.errors.subFileCategoryRequired');
    }
    if (!form.allocationOfficerId) errors.allocationOfficerId = t('workflow.designer.errors.allocationOfficerRequired');
    // if (!form.letterTemplateId) errors.letterTemplateId = 'Letter Template is required.';
    setFormErrors(errors);
    return errors;
  }

  async function persistWorkflowTemplate(
    isDraft: boolean,
    confirmReplaceActive = false,
    stageConfigsOverride?: Record<string, StageConfig>,
  ): Promise<string | false> {
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      // setFlashToast({ message: 'Please complete all required fields before saving.', variant: 'error' });
      return false;
    }

    setSaving(true);
    const resolvedCode = generatesWorkflowCodeLocally()
      ? await refreshWorkflowCodeMetadata()
      : resolveWorkflowCodeForSave();
    const res = await saveWorkflowTemplate({
      workflowTemplateId: workflowTemplateId || undefined,
      workflowCode: resolvedCode || undefined,
      workflowTemplateName: form.workflowTemplateName,
      fileCategoryId: form.fileCategoryId,
      subFileCategoryId: form.subFileCategoryId,
      allocationOfficerId: form.allocationOfficerId,
      letterTemplateIds: form.letterTemplateIds,
      isDraft,
      confirmReplaceActive,
      canvasStages,
      stageConfigs: enrichStageConfigsKpiUnitIds(stageConfigsOverride ?? stageConfigs, timeUnits),
    });
    setSaving(false);

    if (!res.success) {
      if (res.needsConfirmReplace && !isDraft) {
        setReplaceActiveOpen(true);
        return false;
      }
      setFlashToast({ message: res.message, variant: 'error' });
      return false;
    }

    setWorkflowTemplateId(res.data.workflowTemplateId);
    if (res.data.workflowCode) {
      if (!isDraft) setApiWorkflowCode(res.data.workflowCode);
      else commitGeneratedWorkflowCode(res.data.workflowCode);
    }
    if (res.data.versionNo != null) {
      setVersionNo(String(res.data.versionNo));
      if (generatesWorkflowCodeLocally()) {
        updateGeneratedWorkflowCode(form.subFileCategoryId, String(res.data.versionNo));
      }
    }
    if (!isDraft && res.data.status) {
      setStatus(normalizeWorkflowStatusLabel(res.data.status));
    } else if (!isDraft) {
      setStatus('Active');
    }

    if (res.data.stages?.length) {
      const synced = syncDesignerStagesFromSavedStages(
        canvasStages,
        stageConfigsOverride ?? stageConfigs,
        res.data.stages,
      );
      setCanvasStages(synced.canvasStages);
      setStageConfigs(synced.stageConfigs);
      setSelectedStageId((prev) => synced.stageIdMap[prev] ?? prev);
    }

    return res.data.workflowTemplateId;
  }

  async function saveStageConfigOnModalSave(
    stageId: string,
    patch: Partial<StageConfig>,
    successMessage: string,
  ): Promise<boolean> {
    const nextStageConfigs = {
      ...stageConfigs,
      [stageId]: { ...ensureStageConfig(stageId), ...patch },
    };
    setStageConfigs(nextStageConfigs);
    setValidated(false);

    if (isCreate) {
      setFlashToast({ message: successMessage, variant: 'success' });
      return true;
    }

    const savedId = await persistWorkflowTemplate(status !== 'Active', false, nextStageConfigs);
    if (savedId) {
      setFlashToast({ message: successMessage, variant: 'success' });
    }
    return Boolean(savedId);
  }

  function normalizeWorkflowStatusLabel(raw: string): 'Draft' | 'Active' | 'Inactive' {
    const s = raw.trim().toLowerCase();
    if (s === 'active') return 'Active';
    if (s === 'inactive' || s === 'submitted') return 'Inactive';
    return 'Draft';
  }

  function buildValidateInput(resolvedWorkflowCode?: string) {
    const workflowCodeForRequest = (resolvedWorkflowCode ?? resolveWorkflowCodeForSave()).trim();
    return {
      workflowTemplateId: workflowTemplateId || undefined,
      workflowCode: workflowCodeForRequest || undefined,
      workflowTemplateName: form.workflowTemplateName,
      fileCategoryId: form.fileCategoryId,
      subFileCategoryId: form.subFileCategoryId,
      isExemptCategory,
      allocationOfficerId: form.allocationOfficerId,
      letterTemplateIds: form.letterTemplateIds,
      isDraft: status !== 'Active',
      confirmReplaceActive: false,
      canvasStages,
      stageConfigs: enrichStageConfigsKpiUnitIds(stageConfigs, timeUnits),
    };
  }

  function applyValidationResponse(
    data: {
      workflowTemplateId?: string;
      workflowCode?: string;
      versionNo?: string | number;
      isNewTemplate?: boolean;
    },
    valid: boolean,
  ) {
    if (data.workflowTemplateId && data.isNewTemplate !== true) {
      setWorkflowTemplateId(data.workflowTemplateId);
    }
    if (data.workflowCode?.trim()) {
      if (status === 'Active') setApiWorkflowCode(data.workflowCode);
      else commitGeneratedWorkflowCode(data.workflowCode.trim());
    }
    if (valid && data.versionNo != null) {
      setVersionNo(String(data.versionNo));
      if (generatesWorkflowCodeLocally() && !data.workflowCode?.trim()) {
        updateGeneratedWorkflowCode(form.subFileCategoryId, String(data.versionNo));
      }
    }
  }

  async function runValidation() {
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setValidated(false);
      return false;
    }

    setValidating(true);
    try {
      if (workflowMetadataRefreshRef.current) {
        await workflowMetadataRefreshRef.current.catch(() => '');
      }

      const resolvedWorkflowCode = await refreshWorkflowCodeMetadata();
      if (!resolvedWorkflowCode) {
        setFlashToast({
          message: 'Unable to generate a workflow code. Please try again.',
          variant: 'error',
        });
        setValidated(false);
        return false;
      }

      let validateInput = buildValidateInput(resolvedWorkflowCode);

      let res = await validateWorkflowTemplate(validateInput);

      if (res.success && shouldRetryWorkflowCodeValidation(res.data, validateInput)) {
        validateInput = {
          ...validateInput,
          workflowTemplateId: res.data.workflowTemplateId ?? validateInput.workflowTemplateId,
        };
        res = await validateWorkflowTemplate(validateInput);
      }

      if (!res.success) {
        setFlashToast({ message: res.message, variant: 'error' });
        setValidated(false);
        return false;
      }

      setValidationChecks(res.data.checks);
      setValidated(res.data.valid);
      // Persist template id from validate when updating an existing draft — ignore throwaway ids
      // returned with is_new_template (validate may create a temporary draft on the server).
      applyValidationResponse(res.data, res.data.valid);
      setValidationOpen(true);
      return res.data.valid;
    } finally {
      setValidating(false);
    }
  }

  async function handleSaveDraft() {
    const savedId = await persistWorkflowTemplate(true);
    if (!savedId) return;

    setSaving(true);
    const refreshed = await refreshWorkflowTemplateFromApi(savedId);
    setSaving(false);
    if (!refreshed) return;

    setStatus('Draft');
    if (isCreate) {
      skipTemplateLoadRef.current = true;
      setSearchParams({ templateId: savedId, mode: 'edit' }, { replace: true });
    }
    setFlashToast({ message: t('workflow.designer.draftSaved'), variant: 'success' });
  }

  async function handleSaveActiveAllocationOfficer() {
    if (!workflowTemplateId) return;

    const errors: TemplateFormErrors = {};
    if (!form.allocationOfficerId.trim()) {
      errors.allocationOfficerId = t('workflow.designer.errors.allocationOfficerRequired');
    }
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    if (!allocationOfficerChanged) {
      setFlashToast({ message: t('workflow.designer.allocationOfficerUnchanged'), variant: 'info' });
      return;
    }

    setActiveOfficerSaveError('');
    setSaving(true);
    const res = await saveActiveTemplateAllocationOfficer({
      ...buildValidateInput(),
      confirmReplaceActive: false,
    });
    setSaving(false);

    if (!res.success) {
      const message = t('workflow.designer.allocationOfficerSaveFailed');
      setActiveOfficerSaveError(message);
      setFlashToast({ message, variant: 'error' });
      return;
    }

    const refreshed = await refreshWorkflowTemplateFromApi(workflowTemplateId);
    if (!refreshed) return;

    setOriginalAllocationOfficerId(form.allocationOfficerId);
    setActiveOfficerSaveError('');
    setStatus('Active');
    setFlashToast({ message: t('workflow.designer.allocationOfficerSaved'), variant: 'success' });
  }

  async function handleActivate(confirmReplaceActive = true) {
    if (!validated) {
      const ok = await runValidation();
      if (!ok) return;
    }

    const errors = validateForm();
    if (Object.keys(errors).length > 0) return;

    const savedId = await persistWorkflowTemplate(false, confirmReplaceActive);
    if (!savedId) return;

    setStatus('Active');
    setValidated(true);
    setFlashToast({ message: t('workflow.designer.activated'), variant: 'success' });
    nav('/workflow/templates');
  }


  const statusLabel = status;

  if (!isCreate && templateLoadState === 'loading') {
    return (
      <>
        <div className="bc">
          <span>{t('workflow.management')}</span>
          <span className="bc-sep">/</span>
          <Link to="/workflow/templates">{t('workflow.templates.title')}</Link>
          <span className="bc-sep">/</span>
          {t('workflow.designer.title')}
        </div>
        <div className="page-hdr" style={{ marginBottom: 12 }}>
          <div className="pg-title" style={{ marginBottom: 0 }}>
            {t('workflow.designer.title')}
          </div>
        </div>
        <p style={{ color: 'var(--t2)', fontSize: 13 }}>{t('workflow.designer.loading')}</p>
      </>
    );
  }

  if (!isCreate && templateLoadState === 'error') {
    return (
      <>
        <div className="bc">
          <span>{t('workflow.management')}</span>
          <span className="bc-sep">/</span>
          <Link to="/workflow/templates">{t('workflow.templates.title')}</Link>
          <span className="bc-sep">/</span>
          {t('workflow.designer.title')}
        </div>
        <div className="page-hdr" style={{ marginBottom: 12 }}>
          <div className="pg-title" style={{ marginBottom: 0 }}>
            {t('workflow.designer.title')}
          </div>
          <Button variant="primary" onClick={() => nav('/workflow/templates')}>{t('workflow.designer.back')}</Button>
        </div>
        <p style={{ color: 'var(--danger)', fontSize: 13 }}>{t('workflow.designer.loadFailed')}</p>
      </>
    );
  }

  return (
    <>
      <div className="bc">
        <span>{t('workflow.management')}</span>
        <span className="bc-sep">/</span>
        <Link to="/workflow/templates">{t('workflow.templates.title')}</Link>
        <span className="bc-sep">/</span>
        {t('workflow.designer.title')}
      </div>

      <div className="page-hdr" style={{ marginBottom: 12 }}>
        <div className="pg-title" style={{ marginBottom: 0 }}>
          {t('workflow.designer.title')}
        </div>
        <div className="wf-designer-actions">
          {isReadOnly ? (
            <Button variant="primary" onClick={() => nav('/workflow/templates')}>{t('workflow.designer.back')}</Button>
          ) : isActiveOfficerEditMode ? (
            <>
              <Button
                onClick={() => {
                  if (allocationOfficerChanged) setCancelConfirmOpen(true);
                  else nav('/workflow/templates');
                }}
                disabled={saving}
              >
                {t('common.cancel')}
              </Button>
              <Button
                variant="primary"
                onClick={() => void handleSaveActiveAllocationOfficer()}
                disabled={saving || !allocationOfficerChanged}
              >
                {saving ? t('common.saving') : t('workflow.designer.saveAllocationOfficer')}
              </Button>
            </>
          ) : (
            <>
              <Button onClick={() => setCancelConfirmOpen(true)} disabled={saving}>{t('common.cancel')}</Button>
              <Button onClick={() => void handleSaveDraft()} disabled={saving || validating}>
                <ActionIcon name="save" />
                {saving ? t('common.saving') : t('workflow.designer.saveDraft')}
              </Button>
              <Button onClick={() => void runValidation()} disabled={saving || validating}>
                <ActionIcon name="validate" />
                {validating ? t('workflow.designer.validating') : t('workflow.designer.validateWorkflow')}
              </Button>
              <Button variant="primary" onClick={() => void handleActivate()} disabled={!validated || saving || validating}>
                <ActionIcon name="activate" />
                {saving ? t('workflow.designer.activating') : t('workflow.designer.activateWorkflow')}
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="al-filter wf-designer-meta" style={{ marginBottom: 14 }}>
        <div className="wf-designer-meta-row wf-designer-meta-row--6col">
          <FieldInput
            id="workflowTemplateName"
            label={t('workflow.templates.workflowTemplateName')}
            required
            value={form.workflowTemplateName}
            placeholder={t('workflow.templates.templateNamePlaceholder')}
            maxLength={255}
            error={formErrors.workflowTemplateName}
            disabled={isReadOnly || status === 'Active'}
            onChange={(v) => setField('workflowTemplateName', v.replace(/[^a-zA-Z0-9 ]/g, '').slice(0, 255))}
            onBlur={handleWorkflowTemplateNameBlur}
          />
          <FieldInput id="versionNo" label={t('workflow.designer.versionNo')} value={versionNo} disabled readOnly onChange={() => undefined} />
          <FieldInput id="workflowCode" label={t('workflow.templates.workflowCode')} value={displayedWorkflowCode} disabled readOnly onChange={() => undefined} />
          <LookupFieldSelect
            id="fileCategory"
            label={t('admin.shared.fileCategory')}
            required
            value={form.fileCategoryId}
            placeholder={fileCategoriesState === 'loading' ? t('common.loading') : t('workflow.templates.selectFileCategory')}
            options={fileCategories.map((x) => ({ id: x.id, label: x.name }))}
            error={formErrors.fileCategoryId}
            disabled={isReadOnly || status === 'Active' || fileCategoriesState === 'loading'}
            onChange={(v) => setField('fileCategoryId', v)}
          />
          <LookupFieldSelect
            id="subFileCategory"
            label={t('admin.shared.subFileCategory')}
            required={subFileCategoryRequired}
            value={form.subFileCategoryId}
            placeholder={
              !form.fileCategoryId
                ? t('admin.userCategoryForm.selectFileCategoryFirst')
                : subFileCategoriesState === 'loading'
                  ? t('common.loading')
                  : subFileCategoryHasNoOptions
                    ? t('workflow.designer.noSubFileCategoriesAvailable')
                    : t('workflow.templates.selectSubFileCategory')
            }
            options={subFileCategories.map((x) => ({ id: x.id, label: x.name }))}
            error={formErrors.subFileCategoryId}
            disabled={isReadOnly || status === 'Active' || !form.fileCategoryId || subFileCategoriesState === 'loading'}
            onChange={(v) => setField('subFileCategoryId', v)}
          />
          <div className="al-f wf-meta-status">
            <div className="al-l">{t('workflow.designer.status')}</div>
            <div className="wf-meta-status-val">
              <span className={`pill ${status === 'Active' ? 'p-com' : status === 'Inactive' ? 'p-arc' : 'p-pr'}`}>
                {statusLabel}
              </span>
            </div>
          </div>
        </div>
        {showDraftVersionCodeNote ? (
          <div className="wf-designer-meta-row wf-designer-meta-row--6col wf-designer-meta-row--draft-note">
            <p className="wf-meta-temp-assignment-note">
              {t('workflow.designer.draftVersionCodeNote')}
            </p>
          </div>
        ) : null}
        <div className="wf-designer-meta-row wf-designer-meta-row--6col">
          <LookupFieldSelect
            id="allocationOfficer"
            className="wf-meta-alloc"
            label={t('workflow.templates.allocationOfficer')}
            required
            value={form.allocationOfficerId}
            placeholder={allocationOfficersState === 'loading' ? t('common.loading') : t('workflow.templates.selectAllocationOfficer')}
            options={allocationOfficers.map((x) => ({ id: x.id, label: x.label }))}
            error={formErrors.allocationOfficerId}
            disabled={isReadOnly || (status === 'Active' && !isActiveOfficerEditMode) || allocationOfficersState === 'loading'}
            onChange={(v) => setField('allocationOfficerId', v)}
          />
          <div className="al-f wf-meta-letter">
            <div className="al-l">{t('workflow.designer.letterTemplate')}</div>
            <WizardMultiSelectChips
              id="letterTemplate"
              checkboxMenu
              options={letterTemplates.map((x) => ({ id: x.id, label: x.name }))}
              value={form.letterTemplateIds}
              placeholder={
                !form.subFileCategoryId.trim()
                  ? t('workflow.designer.selectSubFileCategoryFirst')
                  : letterTemplatesState === 'loading'
                    ? t('common.loading')
                    : t('workflow.designer.selectLetterTemplate')
              }
              disabled={letterTemplateDisabled}
              hasError={Boolean(formErrors.letterTemplateIds)}
              emptyMessage={t('common.noDataFound')}
              aria-label={t('workflow.designer.letterTemplate')}
              onChange={setLetterTemplateIds}
            />
            {formErrors.letterTemplateIds ? (
              <div style={{ fontSize: 11, color: 'var(--red)', marginTop: 6, fontWeight: 600 }} role="alert">
                {formErrors.letterTemplateIds}
              </div>
            ) : null}
          </div>
        </div>
        {isActiveOfficerEditMode || activeOfficerSaveError ? (
          <div className="wf-designer-meta-hints">
            {isActiveOfficerEditMode ? (
              <p className="wf-designer-meta-hint" role="status">
                {t('workflow.designer.activeOfficerEditNote')}
              </p>
            ) : null}
            {activeOfficerSaveError ? (
              <p className="wf-designer-meta-hint wf-designer-meta-hint--error" role="alert">
                {activeOfficerSaveError}
              </p>
            ) : null}
          </div>
        ) : null}
      </div>

      {showActiveTemplateConflict && !isReadOnly && !isActiveOfficerEditMode ? (
        <div className="wf-val-banner warn" style={{ marginBottom: 14 }}>
          <div aria-hidden="true" style={{ paddingTop: 2 }}>
            <WarningTriangleIcon />
          </div>
          <div style={{ fontSize: 12, lineHeight: 1.6 }}>
            {ACTIVE_TEMPLATE_CONFLICT_WARNING}
          </div>
        </div>
      ) : null}

      <div className="wf-designer-layout">
        <div className="wf-designer-canvas">
          <div className="wf-canvas-row" role="list" aria-label={t('workflow.designer.canvasAriaLabel')}>
            <div className="wf-endcap">Start</div>
            <div className="wf-arrow" aria-hidden="true">
              ›
            </div>

            {canvasStages.map((s, idx) => {
              const on = s.id === selectedStageId;
              const stageNum = idx + 1;
              const isSystem = s.kind !== 'custom';
              const editable = !isReadOnly && status !== 'Active';
              const showAddNext = editable && (s.kind === 'allocation' || s.kind === 'custom');
              const showRemove = editable && s.kind === 'custom';

              return (
                <div key={s.id} className="wf-node-wrap" role="listitem">
                  <div className="wf-node-card">
                    <span className="wf-stage-num" aria-hidden="true">
                      {stageNum}
                    </span>
                    <button
                      type="button"
                      className={`wf-node ${on ? 'on' : ''} ${isSystem ? 'sys' : ''}`}
                      onClick={() => setSelectedStageId(s.id)}
                    >
                      <div className="wf-node-top">
                        <span className="wf-node-badge">{formatStageBadge(stageNum)}</span>
                      </div>
                      <div className="wf-node-title">{s.label}</div>
                    </button>
                  </div>

                  {showAddNext || showRemove ? (
                    <div className="wf-node-actions">
                      {showAddNext ? (
                        <button type="button" className="wf-mini" onClick={() => addStage(s.id)}>
                          {WORKFLOW_ADD_NEXT_STAGE}
                        </button>
                      ) : null}
                      {showRemove ? (
                        <button type="button" className="wf-mini danger" onClick={() => removeStage(s.id)}>
                          {WORKFLOW_REMOVE_STAGE}
                        </button>
                      ) : null}
                    </div>
                  ) : null}

                  <div className="wf-arrow" aria-hidden="true">
                    ›
                  </div>
                </div>
              );
            })}

            <div className="wf-endcap">End</div>
          </div>
        </div>

        <div className="wf-props">
          <div className="wf-props-title">{t('workflow.designer.stageProperties')}</div>
          <div className="cr-table-wrap wf-props-body">
            {selectedStage ? (
              <div style={{ display: 'grid', gap: 10 }}>
                <div className="al-f">
                  <div className="al-l">{t('workflow.designer.selectedStage')}</div>
                  <select
                    className={alSelClass(selectedStageId)}
                    value={selectedStageId}
                    onChange={(e) => setSelectedStageId(e.target.value)}
                    disabled={isReadOnly}
                  >
                    {canvasStages.map((s) => (
                      <option key={s.id} value={s.id}>
                        {formatStageOption(getStageNumber(s.id), s.label)}
                      </option>
                    ))}
                  </select>
                </div>

                {selectedStage.kind === 'custom' && !isReadOnly && status !== 'Active' ? (
                  <FieldInput
                    id="stageName"
                    label={t('workflow.designer.stageName')}
                    required
                    value={selectedStage.label}
                    placeholder={t('workflow.designer.enterStageName')}
                    maxLength={255}
                    onChange={(v) => renameCustomStage(selectedStage.id, v)}
                  />
                ) : null}

                <div className="wf-props-btns">
                  <Button variant="primary" onClick={() => setRoutingOpen(true)}>
                    <ActionIcon name="routing" />
                    {isReadOnly || status === 'Active' ? t('workflow.designer.viewRoutingRules') : t('workflow.designer.configureRoutingRules')}
                  </Button>
                  <Button variant="primary" onClick={() => setKpiOpen(true)}>
                    <ActionIcon name="kpi" />
                    {isReadOnly || status === 'Active' ? t('workflow.designer.viewKpis') : t('workflow.designer.configureKpis')}
                  </Button>
                  <Button variant="primary" onClick={() => setEscalationOpen(true)}>
                    <ActionIcon name="escalation" />
                    {isReadOnly || status === 'Active' ? t('workflow.designer.viewEscalationRules') : t('workflow.designer.configureEscalationRules')}
                  </Button>
                  {selectedStage.kind === 'closure' ? (
                    <Button variant="primary" onClick={() => setAssignmentOpen(true)}>
                      <ActionIcon name="assignment" />
                      {isReadOnly || status === 'Active' ? t('workflow.designer.viewAssignment') : t('workflow.designer.configureAssignment')}
                    </Button>
                  ) : null}
                </div>

                {!isCreate ? (
                  <div className="wf-props-meta">
                    <div className="wf-props-meta-title">{t('workflow.designer.templateUpdates')}</div>
                    <div className="wf-props-meta-grid">
                      <div className="wf-props-meta-card">
                        <div className="wf-props-meta-label">{t('workflow.designer.createdUpdatedAt')}</div>
                        <div className="wf-props-meta-value">{templateUpdatedAt || '—'}</div>
                      </div>
                      <div className="wf-props-meta-card">
                        <div className="wf-props-meta-label">{t('workflow.designer.createdUpdatedBy')}</div>
                        <div className="wf-props-meta-value">{templateUpdatedBy || '—'}</div>
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
            ) : (
              <div style={{ fontSize: 12, color: 'var(--t3)' }}>{t('workflow.designer.selectStageHint')}</div>
            )}
          </div>
        </div>
      </div>

      <ConfirmDialog
        isOpen={cancelConfirmOpen}
        title={t('workflow.designer.cancelWorkflowTitle')}
        message={t('workflow.designer.cancelWorkflowMessage')}
        onClose={() => setCancelConfirmOpen(false)}
        onConfirm={() => nav('/workflow/templates')}
      />

      <ConfirmDialog
        isOpen={replaceActiveOpen}
        title={t('workflow.designer.replaceActiveTitle')}
        message={t('workflow.designer.replaceActiveMessage')}
        onClose={() => setReplaceActiveOpen(false)}
        onConfirm={() => {
          setReplaceActiveOpen(false);
          void handleActivate(true);
        }}
      />

      {routingOpen && selectedStage ? (
        <ConfigureRoutingRulesModal
          key={`routing-${selectedStage.id}`}
          onClose={() => setRoutingOpen(false)}
          readOnly={isReadOnly || status === 'Active'}
          workflowTemplateStageId={resolveWorkflowTemplateStageId(selectedStage)}
          fallbackDefaultNextStage={getNextStageLabel(selectedStage.id)}
          fallbackPreviousStages={getPreviousStageOptions(selectedStage.id)}
          initialAllowedRevert={ensureStageConfig(selectedStage.id).routing?.allowedRevertStageKeys ?? []}
          onSaved={(routing: WorkflowRoutingConfig) =>
            saveStageConfigOnModalSave(selectedStage.id, { routingConfigured: true, routing }, t('workflow.designer.routingSaved'))
          }
        />
      ) : null}

      {kpiOpen && selectedStage ? (
        <ConfigureKpisModal
          key={`kpi-${selectedStage.id}`}
          onClose={() => setKpiOpen(false)}
          readOnly={isReadOnly || status === 'Active'}
          stageLabel={formatStageOption(getStageNumber(selectedStage.id), selectedStage.label)}
          timeUnits={timeUnits}
          timeUnitsState={timeUnitsState}
          initialValue={ensureStageConfig(selectedStage.id).kpis}
          onSaved={(kpis: WorkflowKpiConfig) =>
            saveStageConfigOnModalSave(selectedStage.id, { kpiConfigured: true, kpis }, t('workflow.designer.kpisSaved'))
          }
        />
      ) : null}

      {escalationOpen && selectedStage ? (
        <ConfigureEscalationRulesModal
          key={`escalation-${selectedStage.id}`}
          onClose={() => setEscalationOpen(false)}
          readOnly={isReadOnly || status === 'Active'}
          stageLabel={formatStageOption(getStageNumber(selectedStage.id), selectedStage.label)}
          initialValue={ensureStageConfig(selectedStage.id).escalation}
          onSaved={(escalation: WorkflowEscalationConfig) =>
            saveStageConfigOnModalSave(
              selectedStage.id,
              { escalationConfigured: true, escalation },
              t('workflow.designer.escalationSaved'),
            )
          }
        />
      ) : null}

      {assignmentOpen && selectedStage?.kind === 'closure' ? (
        <ConfigureClosureAssignmentModal
          key={`assignment-${selectedStage.id}`}
          onClose={() => setAssignmentOpen(false)}
          readOnly={isReadOnly || status === 'Active'}
          stageLabel={formatStageOption(getStageNumber(selectedStage.id), selectedStage.label)}
          roles={closureRoles}
          value={ensureStageConfig(selectedStage.id).closureRoleId ?? 0}
          onSaved={(roleId) =>
            saveStageConfigOnModalSave(
              selectedStage.id,
              { closureRoleId: roleId },
              t('workflow.designer.closureSaved'),
            )
          }
        />
      ) : null}

      <WorkflowValidationResultModal
        open={validationOpen}
        checks={validationChecks}
        allPassed={validated}
        onClose={() => setValidationOpen(false)}
      />
    </>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

/** Data dictionary: display name with role, e.g. "N. Nimal Perera (Counsel)". */
function escalationRecipientOptionLabel(u: { displayName: string; roles: string }): string {
  const name = u.displayName.trim();
  const roles = u.roles.trim();
  if (name && roles) return `${name} (${roles})`;
  return name;
}

function WarningTriangleIcon({ size = 22 }: { size?: number }) {
  const gradientId = size === 16 ? 'wf-draft-version-code-warn' : 'wf-active-template-warn';
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      <defs>
        <linearGradient id={gradientId} x1="12" y1="3" x2="12" y2="21" gradientUnits="userSpaceOnUse">
          <stop stopColor="#FDE68A" />
          <stop offset="1" stopColor="#F59E0B" />
        </linearGradient>
      </defs>
      <path
        fill={`url(#${gradientId})`}
        d="M12 3.2 21.2 19.4c.45.78-.12 1.8-1.05 1.8H3.85c-.93 0-1.5-1.02-1.05-1.8L12 3.2z"
      />
      <path fill="#1F2937" d="M11.1 9.5h1.8v5.2h-1.8z" />
      <circle fill="#1F2937" cx="12" cy="17.1" r="1.05" />
    </svg>
  );
}

function ActionIcon({ name }: { name: 'save' | 'validate' | 'activate' | 'routing' | 'kpi' | 'escalation' | 'assignment' }) {
  const common = { width: 14, height: 14, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2 };
  if (name === 'save') {
    return (
      <svg {...common} aria-hidden="true">
        <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
        <polyline points="17 21 17 13 7 13 7 21" />
        <polyline points="7 3 7 8 15 8" />
      </svg>
    );
  }
  if (name === 'validate') {
    return (
      <svg {...common} aria-hidden="true">
        <path d="M9 12l2 2 4-4" />
        <circle cx="12" cy="12" r="9" />
      </svg>
    );
  }
  if (name === 'activate') {
    return (
      <svg {...common} aria-hidden="true">
        <polygon points="5 3 19 12 5 21 5 3" />
      </svg>
    );
  }
  if (name === 'routing') {
    return (
      <svg {...common} aria-hidden="true">
        <circle cx="6" cy="6" r="3" />
        <circle cx="18" cy="18" r="3" />
        <path d="M8.6 8.6l6.8 6.8" />
      </svg>
    );
  }
  if (name === 'kpi') {
    return (
      <svg {...common} aria-hidden="true">
        <line x1="18" y1="20" x2="18" y2="10" />
        <line x1="12" y1="20" x2="12" y2="4" />
        <line x1="6" y1="20" x2="6" y2="14" />
      </svg>
    );
  }
  if (name === 'escalation') {
    return (
      <svg {...common} aria-hidden="true">
        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
        <line x1="12" y1="9" x2="12" y2="13" />
        <line x1="12" y1="17" x2="12.01" y2="17" />
      </svg>
    );
  }
  return (
    <svg {...common} aria-hidden="true">
      <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="8.5" cy="7" r="4" />
      <line x1="20" y1="8" x2="20" y2="14" />
      <line x1="23" y1="11" x2="17" y2="11" />
    </svg>
  );
}

function ChipSelectById({
  value,
  options,
  placeholder,
  onChange,
  readOnly,
}: {
  value: string[];
  options: { id: string; label: string }[];
  placeholder: string;
  onChange: (v: string[]) => void;
  readOnly?: boolean;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const wrapRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const listId = useId();
  const filtered = useMemo(() => {
    const qq = q.trim().toLowerCase();
    return options.filter((o) => (!qq ? true : o.label.toLowerCase().includes(qq)));
  }, [options, q]);

  const selectedLabels = value.map((id) => options.find((o) => o.id === id)?.label ?? id);

  useLayoutEffect(() => {
    if (!open || !wrapRef.current) return;

    const ensureVisible = () => {
      const wrap = wrapRef.current;
      if (!wrap) return;
      const body = wrap.closest('.cn-body') as HTMLElement | null;
      const menu = wrap.querySelector('.chipSelMenu');
      if (!body || !menu) {
        wrap.scrollIntoView({ block: 'nearest' });
        return;
      }
      const bodyRect = body.getBoundingClientRect();
      const menuRect = menu.getBoundingClientRect();
      if (menuRect.bottom > bodyRect.bottom - 8) {
        body.scrollTop += menuRect.bottom - bodyRect.bottom + 12;
      } else if (menuRect.top < bodyRect.top + 8) {
        body.scrollTop -= bodyRect.top - menuRect.top + 12;
      }
    };

    requestAnimationFrame(ensureVisible);
  }, [open, filtered.length, value.length]);

  useEffect(() => {
    if (!open) return;
    const focusTimer = window.setTimeout(() => searchRef.current?.focus(), 0);
    return () => window.clearTimeout(focusTimer);
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) {
        setOpen(false);
        setQ('');
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setOpen(false);
        setQ('');
      }
    };

    const modal = wrapRef.current?.closest('.cn-mbox') as HTMLElement | null | undefined;
    modal?.addEventListener('mousedown', onPointerDown, true);
    window.addEventListener('keydown', onKeyDown, true);
    return () => {
      modal?.removeEventListener('mousedown', onPointerDown, true);
      window.removeEventListener('keydown', onKeyDown, true);
    };
  }, [open]);

  return (
    <div ref={wrapRef} className={`chipSel${open ? ' is-open' : ''}`}>
      <button
        type="button"
        className="chipSelBtn"
        disabled={readOnly}
        aria-expanded={open}
        aria-controls={listId}
        onClick={() => {
          if (readOnly) return;
          if (open) setQ('');
          setOpen(!open);
        }}
      >
        <div className="chipRow">
          {value.length === 0 ? <span className="chipPh">{placeholder}</span> : null}
          {selectedLabels.map((label, i) => (
            <span key={value[i]} className="chip">
              {label}
              {!readOnly ? (
                <button
                  type="button"
                  className="chipX"
                  onClick={(e) => {
                    e.stopPropagation();
                    onChange(value.filter((x) => x !== value[i]));
                  }}
                >
                  ×
                </button>
              ) : null}
            </span>
          ))}
        </div>
        <span className="chipCaret" aria-hidden="true">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </span>
      </button>
      {open && !readOnly ? (
        <div id={listId} className="chipSelMenu" role="listbox">
          <input
            ref={searchRef}
            className="chipSelSearch"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search…"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          />
          <div className="chipSelList">
            {filtered.length === 0 ? (
              <div className="chipSelEmpty">{t('common.noOptionsFound')}</div>
            ) : (
              filtered.map((o) => {
                const checked = value.includes(o.id);
                return (
                  <label key={o.id} className="chipSelOpt">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) =>
                        e.target.checked ? onChange([...value, o.id]) : onChange(value.filter((x) => x !== o.id))
                      }
                    />
                    <span>{o.label}</span>
                  </label>
                );
              })
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function ConfigureRoutingRulesModal({
  onClose,
  workflowTemplateStageId,
  fallbackDefaultNextStage,
  fallbackPreviousStages,
  initialAllowedRevert,
  readOnly,
  onSaved,
}: {
  onClose: () => void;
  workflowTemplateStageId?: string;
  fallbackDefaultNextStage: string;
  fallbackPreviousStages: { id: string; label: string }[];
  initialAllowedRevert: string[];
  readOnly?: boolean;
  onSaved: (routing: WorkflowRoutingConfig) => Promise<boolean>;
}) {
  const { t } = useTranslation();
  const [defaultNextStage, setDefaultNextStage] = useState(fallbackDefaultNextStage);
  const [previousStages, setPreviousStages] = useState(fallbackPreviousStages);
  const [allowedRevert, setAllowedRevert] = useState(initialAllowedRevert);
  const [loading, setLoading] = useState(Boolean(workflowTemplateStageId));
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [cancelConfirm, setCancelConfirm] = useState(false);

  function requestClose() {
    if (readOnly) onClose();
    else setCancelConfirm(true);
  }

  useEffect(() => {
    if (!workflowTemplateStageId) return;

    const controller = new AbortController();
    void (async () => {
      const res = await getWorkflowTemplateStageRevertOptions({
        workflowTemplateStageId,
        signal: controller.signal,
      });
      if (controller.signal.aborted) return;
      setLoading(false);
      if (!res.success) {
        setFlashToast({ message: res.message, variant: 'error' });
        setDefaultNextStage(fallbackDefaultNextStage);
        setPreviousStages(fallbackPreviousStages);
        setAllowedRevert(initialAllowedRevert);
        return;
      }

      const options = res.data;
      setDefaultNextStage(
        resolveDefaultNextStageLabel(options, workflowTemplateStageId, fallbackDefaultNextStage),
      );

      const selectedFromApi = options.filter((o) => o.revertAllowed).map((o) => o.id);
      const mappedInitial = mapRevertKeysToStageIds(
        initialAllowedRevert,
        options,
        fallbackPreviousStages,
      );
      const allowedIds = selectedFromApi.length > 0 ? selectedFromApi : mappedInitial;

      setPreviousStages(buildRevertStageOptions(options, fallbackPreviousStages, allowedIds));
      setAllowedRevert(allowedIds);
    })();

    return () => controller.abort();
  }, [workflowTemplateStageId, fallbackDefaultNextStage, fallbackPreviousStages, initialAllowedRevert]);

  async function handleSave() {
    const routing: WorkflowRoutingConfig = { allowedRevertStageKeys: allowedRevert };
    setSaving(true);
    const ok = await onSaved(routing);
    setSaving(false);
    if (ok) onClose();
  }

  return (
    <>
      <Modal open title={t('workflow.designer.routing.title')} onClose={requestClose} boxClassName="cn-mbox-form">
        <div className="cn-mbox-scroll">
          {loading ? (
            <div style={{ fontSize: 12, color: 'var(--t3)', padding: '8px 0' }}>{t('workflow.designer.routing.loading')}</div>
          ) : (
            <div style={{ display: 'grid', gap: 14 }}>
              <div className="al-f">
                <div className="al-l">{t('workflow.designer.routing.defaultNextStage')}</div>
                <input className="al-inp" value={defaultNextStage} readOnly />
              </div>
              <div className="al-f">
                <div className="al-l">{t('workflow.designer.routing.allowedRevertStages')}</div>
                <ChipSelectById
                  value={allowedRevert}
                  options={previousStages}
                  placeholder={t('workflow.designer.routing.selectStages')}
                  readOnly={readOnly}
                  onChange={(v) => {
                    setAllowedRevert(v);
                    setFormError(null);
                  }}
                />
                <FieldError message={formError ?? undefined} />
              </div>
            </div>
          )}
        </div>
        <div className="cn-footer">
          <Button onClick={requestClose} disabled={saving}>{t('common.cancel')}</Button>
          {readOnly ? null : (
            <Button variant="primary" disabled={loading || saving} onClick={() => void handleSave()}>
              {saving ? t('common.saving') : t('common.save')}
            </Button>
          )}
        </div>
      </Modal>
      <ConfirmDialog
        isOpen={cancelConfirm}
        title={t('workflow.designer.routing.cancelTitle')}
        message={t('workflow.designer.routing.cancelMessage')}
        onClose={() => setCancelConfirm(false)}
        onConfirm={() => {
          setCancelConfirm(false);
          onClose();
        }}
      />
    </>
  );
}

type KpiUnitLabel = 'Days' | 'Hours' | 'Weeks';

const FALLBACK_KPI_DAYS_ID = '__kpi_days__';
const FALLBACK_KPI_HOURS_ID = '__kpi_hours__';
const FALLBACK_KPI_WEEKS_ID = '__kpi_weeks__';
const KPI_TIME_UNIT_CODES = ['DAYS', 'HOURS', 'WEEKS'] as const;
const KPI_TIME_UNIT_ORDER: Record<(typeof KPI_TIME_UNIT_CODES)[number], number> = {
  DAYS: 0,
  HOURS: 1,
  WEEKS: 2,
};

const FALLBACK_KPI_TIME_UNITS: TimeUnitRow[] = [
  { id: FALLBACK_KPI_DAYS_ID, code: 'DAYS', name: 'Days', description: null, active: true },
  { id: FALLBACK_KPI_HOURS_ID, code: 'HOURS', name: 'Hours', description: null, active: true },
  { id: FALLBACK_KPI_WEEKS_ID, code: 'WEEKS', name: 'Weeks', description: null, active: true },
];

function kpiOnlyDigitsMax3(value: string): string {
  return value.replace(/[^\d]/g, '').slice(0, 3);
}

function filterKpiTimeUnits(units: TimeUnitRow[]): TimeUnitRow[] {
  return units.filter(
    (x) => x.active !== false && KPI_TIME_UNIT_CODES.includes((x.code ?? '').toUpperCase() as (typeof KPI_TIME_UNIT_CODES)[number]),
  );
}

function sortKpiTimeUnits(units: TimeUnitRow[]): TimeUnitRow[] {
  return [...units].sort((a, b) => {
    const aCode = (a.code ?? '').toUpperCase() as (typeof KPI_TIME_UNIT_CODES)[number];
    const bCode = (b.code ?? '').toUpperCase() as (typeof KPI_TIME_UNIT_CODES)[number];
    return (KPI_TIME_UNIT_ORDER[aCode] ?? 99) - (KPI_TIME_UNIT_ORDER[bCode] ?? 99);
  });
}

function kpiTimeUnitsWithFallback(units: TimeUnitRow[]): TimeUnitRow[] {
  const filtered = sortKpiTimeUnits(filterKpiTimeUnits(units));
  return filtered.length > 0 ? filtered : FALLBACK_KPI_TIME_UNITS;
}

function isFallbackKpiUnitId(unitId: string): boolean {
  return unitId === FALLBACK_KPI_DAYS_ID || unitId === FALLBACK_KPI_HOURS_ID || unitId === FALLBACK_KPI_WEEKS_ID;
}

function kpiTimeUnitDisplayLabel(unit: TimeUnitRow): KpiUnitLabel {
  const code = (unit.code ?? '').toUpperCase();
  if (code === 'HOURS' || /hour/i.test(unit.name ?? '')) return 'Hours';
  if (code === 'WEEKS' || /week/i.test(unit.name ?? '')) return 'Weeks';
  return 'Days';
}

function defaultDaysUnitId(units: TimeUnitRow[]): string {
  const days = units.find((x) => (x.code ?? '').toUpperCase() === 'DAYS');
  if (days?.id) return days.id;
  const byName = units.find((x) => /day/i.test(x.name ?? ''));
  return byName?.id ?? '';
}

function timeUnitIdFromApiName(units: TimeUnitRow[], name: string): string {
  const n = name.trim().toUpperCase();
  const match =
    units.find((u) => (u.name ?? '').toUpperCase() === n) ??
    units.find((u) => (u.code ?? '').toUpperCase() === n);
  if (match?.id) return match.id;
  if (n.includes('HOUR')) {
    return units.find((u) => (u.code ?? '').toUpperCase() === 'HOURS')?.id ?? defaultDaysUnitId(units);
  }
  if (n.includes('WEEK')) {
    return units.find((u) => (u.code ?? '').toUpperCase() === 'WEEKS')?.id ?? defaultDaysUnitId(units);
  }
  return defaultDaysUnitId(units);
}

function kpiToComparableHours(value: number, unitId: string, units: TimeUnitRow[]): number | null {
  const unit = units.find((x) => x.id === unitId);
  if (!unit) return null;
  const code = (unit.code ?? '').toUpperCase();
  if (code === 'HOURS') return value;
  if (code === 'DAYS') return value * 24;
  if (code === 'WEEKS') return value * 7 * 24;
  const name = (unit.name ?? '').toUpperCase();
  if (name.includes('HOUR')) return value;
  if (name.includes('WEEK')) return value * 7 * 24;
  if (name.includes('DAY')) return value * 24;
  return value;
}

function validateKpiForm(input: {
  ttc: number;
  ttcTimeUnitId: string;
  ttr: number;
  ttrTimeUnitId: string;
  tte: number;
  tteTimeUnitId: string;
  timeUnits: TimeUnitRow[];
}): string | null {
  const hasTtc = input.ttc > 0;
  const hasTtr = input.ttr > 0;
  const hasTte = input.tte > 0;
  if (!hasTtc || !hasTtr || !hasTte) return null;

  if (!input.ttcTimeUnitId || !input.ttrTimeUnitId || !input.tteTimeUnitId) {
    return 'Time unit is required for all KPI fields.';
  }

  const ttcHours = kpiToComparableHours(input.ttc, input.ttcTimeUnitId, input.timeUnits);
  const ttrHours = kpiToComparableHours(input.ttr, input.ttrTimeUnitId, input.timeUnits);
  const tteHours = kpiToComparableHours(input.tte, input.tteTimeUnitId, input.timeUnits);
  if (ttcHours == null || ttrHours == null || tteHours == null) {
    return 'Time units are unavailable. Please try again.';
  }

  if (ttrHours >= ttcHours) return 'Time to Remind must be less than Time to Complete.';
  if (tteHours <= ttrHours || tteHours <= ttcHours) {
    return 'Time to Escalate must be greater than Time to Remind and Time to Complete.';
  }
  return null;
}

function resolveKpiUnitId(unitId: string | undefined, kpiUnits: TimeUnitRow[], daysId: string): string {
  if (unitId && kpiUnits.some((u) => u.id === unitId)) return unitId;
  return daysId;
}

function kpiUnitLabelFromId(units: TimeUnitRow[], unitId: string): KpiUnitLabel {
  const unit = units.find((u) => u.id === unitId);
  if (!unit) return 'Days';
  return kpiTimeUnitDisplayLabel(unit);
}

function kpiUnitIdFromLabel(units: TimeUnitRow[], label: KpiUnitLabel): string {
  const code = label === 'Hours' ? 'HOURS' : label === 'Weeks' ? 'WEEKS' : 'DAYS';
  const match =
    units.find((u) => (u.code ?? '').toUpperCase() === code) ??
    units.find((u) =>
      label === 'Hours' ? /hour/i.test(u.name ?? '') : label === 'Weeks' ? /week/i.test(u.name ?? '') : /day/i.test(u.name ?? ''),
    );
  if (match?.id) return match.id;
  if (label === 'Hours') return FALLBACK_KPI_HOURS_ID;
  if (label === 'Weeks') return FALLBACK_KPI_WEEKS_ID;
  return FALLBACK_KPI_DAYS_ID;
}

function kpiValueToDisplay(value: number | null | undefined): string {
  if (value == null || value <= 0) return '';
  return String(value);
}

function parseKpiInputValue(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n <= 0) return null;
  return n;
}

function resolveKpiUnitIdForSave(
  unitId: string,
  units: TimeUnitRow[],
  kpiTimeUnits: TimeUnitRow[],
): string | null {
  const resolved = isFallbackKpiUnitId(unitId)
    ? kpiUnitIdFromLabel(units, kpiUnitLabelFromId(kpiTimeUnits, unitId))
    : unitId;
  return resolved?.trim() || null;
}

function resolveKpiFormState(initialValue: WorkflowKpiConfig | undefined, timeUnits: TimeUnitRow[]) {
  const kpiUnits = kpiTimeUnitsWithFallback(timeUnits);
  const daysId = defaultDaysUnitId(kpiUnits);
  if (initialValue) {
    return {
      ttc: kpiValueToDisplay(initialValue.ttc),
      ttr: kpiValueToDisplay(initialValue.ttr),
      tte: kpiValueToDisplay(initialValue.tte),
      ttcTimeUnitId: resolveKpiUnitId(initialValue.ttcTimeUnitId ?? '', kpiUnits, daysId),
      ttrTimeUnitId: resolveKpiUnitId(initialValue.ttrTimeUnitId ?? '', kpiUnits, daysId),
      tteTimeUnitId: resolveKpiUnitId(initialValue.tteTimeUnitId ?? '', kpiUnits, daysId),
    };
  }
  return {
    ttc: '',
    ttr: '',
    tte: '',
    ttcTimeUnitId: daysId,
    ttrTimeUnitId: daysId,
    tteTimeUnitId: daysId,
  };
}

function remapKpiTimeUnitId(current: string, timeUnits: TimeUnitRow[]): string {
  const units = kpiTimeUnitsWithFallback(timeUnits);
  const daysId = defaultDaysUnitId(units);
  return !current || isFallbackKpiUnitId(current) ? resolveKpiUnitId(current, units, daysId) : current;
}

function ConfigureKpisModal({
  onClose,
  stageLabel,
  timeUnits,
  timeUnitsState = 'idle',
  initialValue,
  readOnly,
  onSaved,
}: {
  onClose: () => void;
  stageLabel: string;
  timeUnits: TimeUnitRow[];
  timeUnitsState?: LoadState;
  initialValue?: WorkflowKpiConfig;
  readOnly?: boolean;
  onSaved: (kpis: WorkflowKpiConfig) => Promise<boolean>;
}) {
  const { t } = useTranslation();
  const kpiDefaults = resolveKpiFormState(initialValue, timeUnits);
  const [ttc, setTtc] = useState(kpiDefaults.ttc);
  const [ttr, setTtr] = useState(kpiDefaults.ttr);
  const [tte, setTte] = useState(kpiDefaults.tte);
  const [ttcTimeUnitId, setTtcTimeUnitId] = useState(kpiDefaults.ttcTimeUnitId);
  const [ttrTimeUnitId, setTtrTimeUnitId] = useState(kpiDefaults.ttrTimeUnitId);
  const [tteTimeUnitId, setTteTimeUnitId] = useState(kpiDefaults.tteTimeUnitId);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [cancelConfirm, setCancelConfirm] = useState(false);

  const kpiTimeUnits = useMemo(() => kpiTimeUnitsWithFallback(timeUnits), [timeUnits]);
  const timeUnitSelectOptions = useMemo(
    () =>
      kpiTimeUnits.map((u) => ({
        id: u.id,
        label: u.name || u.code || u.id,
      })),
    [kpiTimeUnits],
  );

  function requestClose() {
    if (readOnly) onClose();
    else setCancelConfirm(true);
  }


  async function handleSave() {
    const ttcNum = parseKpiInputValue(ttc);
    const ttrNum = parseKpiInputValue(ttr);
    const tteNum = parseKpiInputValue(tte);
    const validationError = validateKpiForm({
      ttc: ttcNum ?? 0,
      ttcTimeUnitId: ttcTimeUnitId ?? '',
      ttr: ttrNum ?? 0,
      ttrTimeUnitId: ttrTimeUnitId ?? '',
      tte: tteNum ?? 0,
      tteTimeUnitId: tteTimeUnitId ?? '',
      timeUnits: kpiTimeUnits,
    });
    if (validationError) {
      setFormError(validationError);
      return;
    }

    const units = kpiTimeUnitsWithFallback(timeUnits);
    const kpis: WorkflowKpiConfig = {
      ttc: ttcNum,
      ttr: ttrNum,
      tte: tteNum,
      ttcTimeUnitId: ttcNum != null ? resolveKpiUnitIdForSave(ttcTimeUnitId, units, kpiTimeUnits) : null,
      ttrTimeUnitId: ttrNum != null ? resolveKpiUnitIdForSave(ttrTimeUnitId, units, kpiTimeUnits) : null,
      tteTimeUnitId: tteNum != null ? resolveKpiUnitIdForSave(tteTimeUnitId, units, kpiTimeUnits) : null,
    };

    setSaving(true);
    const ok = await onSaved(kpis);
    setSaving(false);
    if (ok) onClose();
  }

  return (
    <>
      <Modal open title={t('workflow.designer.kpiModal.title')} onClose={requestClose} boxClassName="cn-mbox-form">
        <div className="cn-mbox-scroll">
          <div className="ccr-fr c2" style={{ marginBottom: 0 }}>
            <div className="ccr-fg" style={{ gridColumn: '1 / -1' }}>
              <label>{t('workflow.kpi.workflowStage')}</label>
              <input className="ccr-inp tcd-ro-field" type="text" value={stageLabel} readOnly disabled />
            </div>

            <hr className="wf-modal-section-divider" style={{ gridColumn: '1 / -1' }} />

            <div className="ccr-fg" style={{ gridColumn: '1 / -1' }}>
              <label>{t('workflow.kpi.kpisTitle')}</label>
            </div>

            <KpiTimeField
              id="kpi-ttc"
              label={t('workflow.kpi.timeToComplete')}
              value={ttc}
              unitId={remapKpiTimeUnitId(ttcTimeUnitId, timeUnits)}
              timeUnitOptions={timeUnitSelectOptions}
              timeUnitsState={timeUnitsState}
              readOnly={readOnly}
              hasError={Boolean(formError)}
              onValue={(v) => {
                setTtc(v);
                setFormError(null);
              }}
              onUnit={(v) => {
                setTtcTimeUnitId(v);
                setFormError(null);
              }}
              placeholder={t('workflow.designer.kpiModal.ttcPlaceholder')}
            />
            <KpiTimeField
              id="kpi-ttr"
              label={t('workflow.kpi.timeToRemind')}
              value={ttr}
              unitId={remapKpiTimeUnitId(ttrTimeUnitId, timeUnits)}
              timeUnitOptions={timeUnitSelectOptions}
              timeUnitsState={timeUnitsState}
              readOnly={readOnly}
              hasError={Boolean(formError)}
              onValue={(v) => {
                setTtr(v);
                setFormError(null);
              }}
              onUnit={(v) => {
                setTtrTimeUnitId(v);
                setFormError(null);
              }}
              placeholder={t('workflow.designer.kpiModal.ttrPlaceholder')}
            />
            <KpiTimeField
              id="kpi-tte"
              label={t('workflow.kpi.timeToEscalate')}
              value={tte}
              unitId={remapKpiTimeUnitId(tteTimeUnitId, timeUnits)}
              timeUnitOptions={timeUnitSelectOptions}
              timeUnitsState={timeUnitsState}
              readOnly={readOnly}
              hasError={Boolean(formError)}
              onValue={(v) => {
                setTte(v);
                setFormError(null);
              }}
              onUnit={(v) => {
                setTteTimeUnitId(v);
                setFormError(null);
              }}
              placeholder={t('workflow.designer.kpiModal.ttePlaceholder')}
            />

            <div className="ccr-fg" style={{ gridColumn: '1 / -1' }}>
              <FieldError message={formError ?? undefined} />
            </div>
          </div>
        </div>
        <div className="cn-footer">
          <Button onClick={requestClose} disabled={saving}>{t('common.cancel')}</Button>
          {readOnly ? null : (
            <Button variant="primary" disabled={saving} onClick={() => void handleSave()}>
              {saving ? t('common.saving') : t('common.save')}
            </Button>
          )}
        </div>
      </Modal>
      <ConfirmDialog
        isOpen={cancelConfirm}
        title={t('workflow.designer.kpiModal.cancelTitle')}
        message={t('workflow.designer.kpiModal.cancelMessage')}
        onClose={() => setCancelConfirm(false)}
        onConfirm={() => {
          setCancelConfirm(false);
          onClose();
        }}
      />
    </>
  );
}

function KpiTimeField({
  id,
  label,
  value,
  unitId,
  timeUnitOptions,
  timeUnitsState = 'idle',
  readOnly,
  hasError,
  onValue,
  onUnit,
  placeholder
}: {
  id: string;
  label: string;
  value: string;
  unitId: string;
  timeUnitOptions: { id: string; label: string }[];
  timeUnitsState?: LoadState;
  readOnly?: boolean;
  hasError?: boolean;
  onValue: (v: string) => void;
  onUnit: (v: string) => void;
  placeholder?: string;
}) {
  const unitLoading = timeUnitsState === 'loading';
  const unitDisabled = readOnly || unitLoading || timeUnitsState === 'error';

  return (
    <div className="ccr-fg">
      <label>{label}</label>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px', gap: 10 }}>
        <input
          id={id}
          className={inpClass(Boolean(hasError))}
          type="text"
          inputMode="numeric"
          pattern="\\d*"
          maxLength={3}
          value={value}
          readOnly={readOnly}
          disabled={readOnly}
          placeholder={placeholder}
          onChange={(e) => onValue(kpiOnlyDigitsMax3(e.target.value))}
        />
        <WizardSelect
          id={`${id}-unit`}
          value={unitId}
          placeholder={unitLoading ? 'Loading…' : 'Select unit'}
          options={timeUnitOptions}
          disabled={unitDisabled}
          loading={unitLoading}
          hasError={Boolean(hasError)}
          clearable={false}
          onChange={onUnit}
        />
      </div>
    </div>
  );
}

function ConfigureEscalationRulesModal({
  onClose,
  stageLabel,
  initialValue,
  readOnly,
  onSaved,
}: {
  onClose: () => void;
  stageLabel: string;
  initialValue?: WorkflowEscalationConfig;
  readOnly?: boolean;
  onSaved: (escalation: WorkflowEscalationConfig) => Promise<boolean>;
}) {
  const { t } = useTranslation();
  const [recipients, setRecipients] = useState(initialValue?.escalationRecipientUserIds ?? []);
  const [recipientOptions, setRecipientOptions] = useState<{ id: string; label: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [cancelConfirm, setCancelConfirm] = useState(false);

  function requestClose() {
    if (readOnly) onClose();
    else setCancelConfirm(true);
  }

  useEffect(() => {
    const controller = new AbortController();
    void (async () => {
      const usersRes = await getEscalationEligibleUsers({ signal: controller.signal });
      if (controller.signal.aborted) return;
      setLoading(false);

      const optionById = new Map<string, { id: string; label: string }>();

      for (const user of initialValue?.escalationRecipientUsers ?? []) {
        optionById.set(user.id, { id: user.id, label: user.name });
      }

      if (usersRes.success) {
        for (const u of usersRes.data) {
          optionById.set(u.userId, {
            id: u.userId,
            label: escalationRecipientOptionLabel(u),
          });
        }
      } else {
        setFlashToast({ message: usersRes.message, variant: 'error' });
      }

      for (const id of initialValue?.escalationRecipientUserIds ?? []) {
        if (!optionById.has(id)) optionById.set(id, { id, label: id });
      }

      setRecipientOptions([...optionById.values()]);
    })();

    return () => controller.abort();
  }, [initialValue]);

  async function handleSave() {
    const escalation: WorkflowEscalationConfig = { escalationRecipientUserIds: recipients };
    setSaving(true);
    const ok = await onSaved(escalation);
    setSaving(false);
    if (ok) onClose();
  }

  return (
    <>
      <Modal open title={t('workflow.designer.escalation.title')} onClose={requestClose} boxClassName="cn-mbox-form">
        <div className="cn-mbox-scroll">
          {loading ? (
            <div style={{ fontSize: 12, color: 'var(--t3)', padding: '8px 0' }}>{t('common.loading')}</div>
          ) : (
            <div className="ccr-fr c2" style={{ marginBottom: 0 }}>
              <div className="ccr-fg" style={{ gridColumn: '1 / -1' }}>
                <label>{t('workflow.kpi.workflowStage')}</label>
                <input className="ccr-inp tcd-ro-field" type="text" value={stageLabel} readOnly disabled />
              </div>

              <hr className="wf-modal-section-divider" style={{ gridColumn: '1 / -1' }} />

              <div className="ccr-fg" style={{ gridColumn: '1 / -1' }}>
                <label>{t('workflow.escalationRules.hierarchyTitle')}</label>
                <div className="esc-wrap esc-wrap-modal">
                  <div className="esc-line" aria-hidden="true" />
                  <div className="esc-row">
                    <div className="esc-ico esc-reminder" aria-hidden="true">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                        <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                      </svg>
                    </div>
                    <div style={{ flex: 1 }}>
                      <div className="esc-title">{t('workflow.escalationRules.reminderRecipient')}</div>
                      <input className="ccr-inp tcd-ro-field" type="text" value={ASSIGNED_USER_RECIPIENT} readOnly disabled />
                    </div>
                  </div>
                  <div className="esc-row">
                    <div className="esc-ico esc-escalate" aria-hidden="true">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                        <line x1="12" y1="9" x2="12" y2="13" />
                        <line x1="12" y1="17" x2="12.01" y2="17" />
                      </svg>
                    </div>
                    <div style={{ flex: 1 }}>
                      <div className="esc-title">{t('workflow.escalationRules.escalationLevel1')}</div>
                      <ChipSelectById
                        value={recipients}
                        options={recipientOptions}
                        placeholder={t('workflow.escalationRules.selectUsers')}
                        readOnly={readOnly}
                        onChange={(v) => {
                          setRecipients(v);
                          setFormError(null);
                        }}
                      />
                      <FieldError message={formError ?? undefined} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="cn-footer">
          <Button onClick={requestClose} disabled={saving}>{t('common.cancel')}</Button>
          {readOnly ? null : (
            <Button variant="primary" disabled={loading || saving} onClick={() => void handleSave()}>
              {saving ? t('common.saving') : t('common.save')}
            </Button>
          )}
        </div>
      </Modal>
      <ConfirmDialog
        isOpen={cancelConfirm}
        title={t('workflow.designer.escalation.cancelTitle')}
        message={t('workflow.designer.escalation.cancelMessage')}
        onClose={() => setCancelConfirm(false)}
        onConfirm={() => {
          setCancelConfirm(false);
          onClose();
        }}
      />
    </>
  );
}

function ConfigureClosureAssignmentModal({
  onClose,
  stageLabel,
  roles,
  value,
  readOnly,
  onSaved,
}: {
  onClose: () => void;
  stageLabel: string;
  roles: ActiveRoleRow[];
  value: number;
  readOnly?: boolean;
  onSaved: (roleId: number) => Promise<boolean>;
}) {
  const { t } = useTranslation();
  const [roleId, setRoleId] = useState(value ? String(value) : '');
  const [stageExpanded, setStageExpanded] = useState(true);
  const [saving, setSaving] = useState(false);
  const [cancelConfirm, setCancelConfirm] = useState(false);

  function requestClose() {
    if (readOnly) onClose();
    else setCancelConfirm(true);
  }

  async function handleSave() {
    const selectedRoleId = Number(roleId);
    if (!selectedRoleId) return;
    setSaving(true);
    const ok = await onSaved(selectedRoleId);
    setSaving(false);
    if (ok) onClose();
  }

  return (
    <>
      <Modal open title={t('workflow.designer.assignment.title')} onClose={requestClose} boxClassName="cn-mbox-form">
        <div className="cn-mbox-scroll">
          <div style={{ display: 'grid', gap: 14 }}>
            <button
              type="button"
              className="wf-modal-stage-hdr"
              onClick={() => setStageExpanded((v) => !v)}
              aria-expanded={stageExpanded}
            >
              <span>{stageLabel}</span>
              <span className="wf-modal-stage-caret" aria-hidden="true">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points={stageExpanded ? '18 15 12 9 6 15' : '6 9 12 15 18 9'} />
                </svg>
              </span>
            </button>
            {stageExpanded ? (
              <div className="al-f">
                <div className="al-l">
                  {t('workflow.designer.assignment.selectUserRole')} <span className="req">*</span>
                </div>
                <select
                  className={alSelClass(roleId)}
                  value={roleId}
                  disabled={readOnly}
                  onChange={(e) => setRoleId(e.target.value)}
                >
                  <option value="" disabled hidden>
                    {t('workflow.designer.assignment.selectUserRole')}
                  </option>
                  {roles.map((r) => (
                    <option key={r.roleId} value={String(r.roleId)}>
                      {r.roleName}
                    </option>
                  ))}
                </select>
              </div>
            ) : null}
          </div>
        </div>
        <div className="cn-footer">
          <Button onClick={requestClose} disabled={saving}>{t('common.cancel')}</Button>
          {readOnly ? null : (
            <Button variant="primary" disabled={!roleId || saving} onClick={() => void handleSave()}>
              {saving ? t('common.saving') : t('common.save')}
            </Button>
          )}
        </div>
      </Modal>
      <ConfirmDialog
        isOpen={cancelConfirm}
        title={t('workflow.designer.assignment.cancelTitle')}
        message={t('workflow.designer.assignment.cancelMessage')}
        onClose={() => setCancelConfirm(false)}
        onConfirm={() => {
          setCancelConfirm(false);
          onClose();
        }}
      />
    </>
  );
}

function WorkflowValidationResultModal({
  open,
  checks,
  allPassed,
  onClose,
}: {
  open: boolean;
  checks: WorkflowValidationCheck[];
  allPassed: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  return (
    <Modal open={open} title={t('workflow.designer.validationResultTitle')} onClose={onClose}>
      <div style={{ display: 'grid', gap: 16 }}>
        <div style={{ display: 'grid', gap: 14 }}>
          {checks.map((it) => (
            <div key={it.key} style={{ display: 'grid', gridTemplateColumns: '34px 1fr', gap: 12, alignItems: 'center' }}>
              <span
                aria-hidden="true"
                className={`wf-val-icon ${it.passed ? 'ok' : 'fail'}`}
              >
                {it.passed ? '✓' : '×'}
              </span>
              <div className={`wf-val-label ${it.passed ? 'ok' : 'fail'}`}>{it.label}</div>
              {/* {!it.passed && it.actionLabel ? (
                <button className="mfb" type="button" onClick={onClose}>
                  {it.actionLabel}
                </button>
              ) : (
                <div />
              )} */}
            </div>
          ))}
        </div>

        <div className={`wf-val-banner ${allPassed ? 'ok' : 'fail'}`}>
          <div aria-hidden="true" style={{ paddingTop: 2 }}>
            {allPassed ? (
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#1B5E20" strokeWidth="2">
                <path d="M9 12l2 2 4-4" />
                <circle cx="12" cy="12" r="9" />
              </svg>
            ) : (
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#B91C1C" strokeWidth="2">
                <path d="M12 9v4" />
                <path d="M12 17h.01" />
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              </svg>
            )}
          </div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 800, marginBottom: 6 }}>
              {allPassed ? 'Validation Successful' : 'Validation Failed'}
            </div>
            <div style={{ fontSize: 12, lineHeight: 1.6 }}>
              {allPassed
                ? 'All workflow validation checks have passed. The workflow is ready for activation.'
                : 'The workflow template is missing required validation configurations. Please configure all mandatory validation rules before activation.'}
            </div>
          </div>
        </div>
      </div>

      <div className="cn-footer">
        <Button variant="primary" onClick={onClose}>
          {t('workflow.designer.returnToDesigner')}
        </Button>
      </div>
    </Modal>
  );
}

function FieldInput({
  id,
  label,
  required,
  value,
  placeholder,
  onChange,
  onBlur,
  hint,
  error,
  disabled,
  readOnly,
  maxLength,
  className,
}: {
  id?: string;
  label: string;
  required?: boolean;
  value: string;
  placeholder?: string;
  onChange: (v: string) => void;
  onBlur?: () => void;
  hint?: string;
  error?: string;
  disabled?: boolean;
  readOnly?: boolean;
  maxLength?: number;
  className?: string;
}) {
  const errId = error ? `${id}-err` : undefined;
  const hintId = hint ? `${id}-hint` : undefined;

  return (
    <div className={['al-f', className].filter(Boolean).join(' ')}>
      <div className="al-l">
        {label} {required ? <span className="req">*</span> : null}
      </div>
      <input
        id={id}
        className="al-inp"
        value={value}
        placeholder={placeholder}
        maxLength={maxLength}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        disabled={disabled}
        readOnly={readOnly}
        style={error ? { borderColor: 'var(--red)' } : undefined}
        aria-invalid={error ? true : undefined}
        aria-describedby={[errId, hintId].filter(Boolean).join(' ') || undefined}
      />
      {error ? (
        <div id={errId} style={{ fontSize: 11, color: 'var(--red)', marginTop: 6, fontWeight: 600 }}>
          {error}
        </div>
      ) : null}
      {hint ? (
        <div id={hintId} style={{ fontSize: 11, color: 'var(--t3)', marginTop: error ? 4 : 6 }}>
          {hint}
        </div>
      ) : null}
    </div>
  );
}